import {
  type DemoAccount,
  isBelowThreshold,
  type ResetDemoErrorType,
  ResetDemoPanel,
  type ResetDemoResult,
  useDemoAccountsState,
} from '@frontends/feature-demo-balance';
import { createDemoAccountsApi } from '@frontends/feature-demo-balance/api';
import { formatCurrency } from '@frontends/util-utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { showToast } from '@/components/Toast';
import useAuth from '@/hooks/useAuth';
import { useEntityConfig } from '@/hooks/useEntityConfig';
import { axiosPrivate } from '@/lib/axios';

interface ClientAccountSummary {
  accountId: number;
  accountNumber: number;
  createTime: string;
}

export function AccountsLayout() {
  const { t, i18n } = useTranslation('translation');
  const config = useEntityConfig();
  const { client, maskLogin } = useAuth();
  const [searchParams] = useSearchParams();

  const clientAccounts = useMemo<ClientAccountSummary[]>(
    () => (client?.client_details?.accounts ?? []) as ClientAccountSummary[],
    [client?.client_details?.accounts],
  );

  const logins = useMemo(() => clientAccounts.map((a) => a.accountNumber), [clientAccounts]);

  const api = useMemo(
    () =>
      createDemoAccountsApi({
        http: axiosPrivate,
        tradingApiBaseUrl: config.VITE_URL_TRADING_API,
        logins,
      }),
    [config.VITE_URL_TRADING_API, logins],
  );

  const [initial, setInitial] = useState<DemoAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const { accounts, applyResetResult } = useDemoAccountsState({ initial });

  const fetchAccounts = useMemo(
    () => async () => {
      setLoading(true);
      setError(null);

      try {
        setInitial(await api.list());
      } catch (e) {
        setError(e as Error);
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const preselectLogin = useMemo<number | null>(() => {
    if (accounts.length === 0) {
      return null;
    }

    const qParam = Number(searchParams.get('account'));
    const fromQ = Number.isFinite(qParam) ? accounts.find((a) => a.login === qParam) : undefined;

    if (fromQ) {
      return fromQ.login;
    }

    const firstLow = accounts.find((a) => isBelowThreshold(a.balance, a.currency));

    return (firstLow ?? accounts[0]).login;
  }, [accounts, searchParams]);

  const [selectedLogin, setSelectedLogin] = useState<number | null>(null);

  useEffect(() => {
    setSelectedLogin(preselectLogin);
  }, [preselectLogin]);

  const handleReset = async (login: number, amount: number): Promise<ResetDemoResult> => {
    const current = accounts.find((a) => a.login === login);

    if (!current) {
      throw new Error(`Account ${login} not in current state`);
    }

    try {
      const result = await api.reset(login, amount);
      applyResetResult(login, result.newBalance);

      showToast({
        type: 'success',
        title: t('demo_balance_reset_success', {
          amount: formatCurrency(amount, current.currency, i18n.language),
        }),
        description: '',
      });

      return result;
    } catch (e) {
      const kind: ResetDemoErrorType = (e as { kind?: ResetDemoErrorType })?.kind ?? 'generic';
      const key =
        kind === 'rejected'
          ? 'demo_balance_reset_rejected'
          : kind === 'too-frequent'
            ? 'demo_balance_reset_too_frequent'
            : 'demo_balance_reset_error';

      showToast({ type: 'error', title: t(key), description: '' });

      throw e;
    }
  };

  return (
    <div className="max-w-2xl rounded-2xl border border-borderSurface bg-backgroundRaised p-6">
      <ResetDemoPanel
        hasHeader
        accounts={accounts}
        selectedLogin={selectedLogin}
        onSelectAccount={setSelectedLogin}
        maskLogin={maskLogin}
        onReset={handleReset}
        isLoading={loading}
        error={error}
        onRetry={fetchAccounts}
      />
    </div>
  );
}
